/**
 * MAX-POWER CONSTRAINT CEILINGS — how much capacity each documented limit allows.
 *
 * `solveGenerationImpact` already knows every limit that matters (firm gas
 * capacity, the pressure-derated interconnect, water consumption, CO2 export,
 * receptor noise) — but it only reports them as PASS/FAIL risks at the
 * scenario's configured size. It never solves the inverse: "what capacity would
 * satisfy this limit?". That inverse is what this module supplies.
 *
 * ═══ TWO RULES THIS MODULE IS BUILT AROUND ═══
 *
 * 1. UNDOCUMENTED IS NEVER UNLIMITED. A missing limit yields `indeterminate`,
 *    never a ceiling of Infinity. The distinction is the whole honesty of the
 *    feature: a site with no documented gas capacity has an UNKNOWN gas ceiling,
 *    and reporting "gas never binds" would be a fabrication. Statuses:
 *      bounded        — a real, documented ceiling
 *      not-binding    — documented, but above the search range
 *      indeterminate  — the limit is not documented, or not computable
 *      not-applicable — the constraint does not exist for this scenario (CCS off)
 *      not-scaling    — the constraint is real but does NOT vary along the swept
 *                       axis, so no ceiling can be derived from it (see A10)
 *
 * 2. RESTRICTED ARITHMETIC. Only + - * / and Math.min/max/abs/ceil/floor. This
 *    output is frozen into report exhibits and byte-compared on validation, so
 *    every value must be exactly reproducible — no Math.pow / log / exp. It is
 *    also the second reason the noise ceiling is ENUMERATED by the sweep rather
 *    than solved in closed form (the first being that the acoustic model has no
 *    MW dependence at all).
 *
 * The linear ceilings below are ANALYTIC: gas, CO2 and generation water are all
 * strictly proportional to site-rated gross MW at a fixed fleet shape, so the
 * ceiling is `limit ÷ (consumption per gross MW)` read off an exact base solve.
 * The sweep still VERIFIES each one with a real solver run — the analytic value
 * only brackets the search.
 */

import type {
  GenerationImpactResult,
  GenerationImpactScenarioInput,
  GenerationMaxPowerCeilingStatus,
  GenerationMaxPowerConstraintCeiling,
  GenerationMaxPowerConstraintId,
} from '../../types/generationImpact';
import { deriveGasInterconnectDerate } from './gasInterconnectDerate';

/** Everything the ceiling math reads, resolved once from an exact base solve. */
export interface MaxPowerCeilingContext {
  input: GenerationImpactScenarioInput;
  result: GenerationImpactResult;
  /** Total site-rated gross MW at the base solve (Σ siteGrossMWPerUnit × count). */
  baseSiteGrossMW: number;
  /** contingencyNetMW ÷ baseSiteGrossMW — the firm-delivery coefficient. */
  firmNetPerGrossMW: number;
}

/** Resolve the context from an input + its exact solve. */
export function maxPowerCeilingContext(
  input: GenerationImpactScenarioInput,
  result: GenerationImpactResult,
): MaxPowerCeilingContext {
  const baseSiteGrossMW = result.unitResults.reduce(
    (sum, row) => sum + row.siteGrossMWPerUnit * row.count,
    0,
  );
  return {
    input,
    result,
    baseSiteGrossMW,
    firmNetPerGrossMW: baseSiteGrossMW > 0 ? result.capacity.contingencyNetMW / baseSiteGrossMW : 0,
  };
}

function indeterminate(
  id: GenerationMaxPowerConstraintId,
  label: string,
  reason: string,
  extra: Partial<GenerationMaxPowerConstraintCeiling> = {},
): GenerationMaxPowerConstraintCeiling {
  return {
    id,
    label,
    status: 'indeterminate',
    basis: 'analytic-linear',
    limitValue: null,
    limitUnit: null,
    intensityPerGrossMW: null,
    ceilingSiteGrossMW: null,
    conservativeCeilingSiteGrossMW: null,
    lastPassingRungIndex: null,
    reason,
    ...extra,
  };
}

/**
 * A ceiling for a constraint that is LINEAR in site-rated gross MW.
 *
 * `ceiling = limit ÷ intensity`, where `intensity` is the consumption per gross
 * MW read off the exact base solve. A zero/non-finite intensity means the
 * constraint does not respond to capacity at all — reported as `not-scaling`,
 * NOT as an infinite ceiling.
 */
function linearCeiling(args: {
  id: GenerationMaxPowerConstraintId;
  label: string;
  limitValue: number | null;
  limitUnit: string;
  baseConsumption: number | null;
  baseSiteGrossMW: number;
  undocumentedReason: string;
  notScalingReason: string;
  conservativeLimitValue?: number | null;
}): GenerationMaxPowerConstraintCeiling {
  const {
    id, label, limitValue, limitUnit, baseConsumption, baseSiteGrossMW,
    undocumentedReason, notScalingReason, conservativeLimitValue,
  } = args;

  if (limitValue == null || !Number.isFinite(limitValue)) {
    return indeterminate(id, label, undocumentedReason, { limitUnit });
  }
  if (baseConsumption == null || !Number.isFinite(baseConsumption) || baseSiteGrossMW <= 0) {
    return indeterminate(id, label, notScalingReason, { limitValue, limitUnit });
  }

  const intensity = baseConsumption / baseSiteGrossMW;
  if (!Number.isFinite(intensity) || intensity <= 0) {
    return {
      id, label,
      status: 'not-scaling',
      basis: 'analytic-linear',
      limitValue, limitUnit,
      intensityPerGrossMW: intensity,
      ceilingSiteGrossMW: null,
      conservativeCeilingSiteGrossMW: null,
      lastPassingRungIndex: null,
      reason: notScalingReason,
    };
  }

  const conservative = conservativeLimitValue != null && Number.isFinite(conservativeLimitValue)
    ? conservativeLimitValue / intensity
    : null;

  return {
    id, label,
    status: 'bounded',
    basis: 'analytic-linear',
    limitValue, limitUnit,
    intensityPerGrossMW: intensity,
    ceilingSiteGrossMW: limitValue / intensity,
    conservativeCeilingSiteGrossMW: conservative,
    lastPassingRungIndex: null,
    reason: null,
  };
}

/** Firm gas capacity: peak nomination is strictly proportional to gross MW. */
function gasFirmCapacityCeiling(ctx: MaxPowerCeilingContext): GenerationMaxPowerConstraintCeiling {
  return linearCeiling({
    id: 'gas-firm-capacity',
    label: 'Firm gas delivery capacity',
    limitValue: ctx.input.availableGasCapacityDthPerDay ?? null,
    limitUnit: 'Dth/day',
    baseConsumption: ctx.result.gas.peakDthPerDay,
    baseSiteGrossMW: ctx.baseSiteGrossMW,
    // Reuse the solver's own risk wording so the study and the risk register
    // can never disagree about what is missing.
    undocumentedReason: 'Firm gas delivery capacity is not documented against calculated peak nomination.',
    notScalingReason: 'The fleet consumes no gas, so firm gas capacity cannot bound it.',
  });
}

/**
 * Pressure-derated interconnect. Reported SEPARATELY from contract capacity —
 * they are deliberately distinct screens in the solver, and a station can pass
 * its contract while failing at the documented minimum delivery pressure.
 *
 * When compressibility is undocumented the derate is IDEAL-GAS and therefore
 * OVER-states deliverability by up to `IDEAL_GAS_DERATE_UNCERTAINTY_FRACTION`
 * one-sided — so the headline ceiling is optimistic and the conservative twin
 * must be shown beside it.
 */
function gasDerateCeiling(ctx: MaxPowerCeilingContext): GenerationMaxPowerConstraintCeiling {
  const supply = ctx.input.projectDutyBasis?.fuelGas?.supply;
  const label = 'Gas interconnect (pressure-derated)';
  if (!supply) {
    return indeterminate(
      'gas-interconnect-derate', label,
      'No fuel-gas interconnect duty is documented, so the pressure derate is not computable.',
    );
  }

  const derate = deriveGasInterconnectDerate(
    supply,
    ctx.input.projectDutyBasis?.fuelGas?.documentedSiteSupplyMinimum,
  );
  if (derate.status !== 'derated' || derate.deratedDeliverableDthPerDay == null) {
    return indeterminate(
      'gas-interconnect-derate', label,
      derate.status === 'minimum-pressure-undocumented'
        ? 'The minimum delivery pressure is undocumented, so the station derate cannot be computed.'
        : 'The gas interconnect derate is not computable from the documented duty.',
    );
  }

  const ceiling = linearCeiling({
    id: 'gas-interconnect-derate',
    label,
    limitValue: derate.deratedDeliverableDthPerDay,
    limitUnit: 'Dth/day (at minimum delivery pressure)',
    baseConsumption: ctx.result.gas.peakDthPerDay,
    baseSiteGrossMW: ctx.baseSiteGrossMW,
    undocumentedReason: 'The pressure-derated deliverable capacity is not documented.',
    notScalingReason: 'The fleet consumes no gas, so the interconnect derate cannot bound it.',
    // One-sided ideal-gas band — only non-zero when compressibility is assumed.
    conservativeLimitValue: derate.deratedDeliverableDthPerDay - derate.uncertaintyDthPerDay,
  });

  if (ceiling.status === 'bounded' && derate.compressibilityBasis === 'ideal-gas-assumed') {
    return {
      ...ceiling,
      reason: 'Compressibility is undocumented, so the derate assumes ideal gas and OVER-states deliverability;'
        + ' treat the conservative ceiling as the defensible figure.',
    };
  }
  return ceiling;
}

/** CO2 export. Annual, so the ceiling is conditional on operating hours. */
function co2ExportCeiling(ctx: MaxPowerCeilingContext): GenerationMaxPowerConstraintCeiling {
  const label = 'CO₂ export capacity';
  if (!ctx.input.ccs.enabled) {
    return {
      id: 'co2-export', label,
      status: 'not-applicable',
      basis: 'analytic-linear',
      limitValue: null, limitUnit: null,
      intensityPerGrossMW: null,
      ceilingSiteGrossMW: null, conservativeCeilingSiteGrossMW: null,
      lastPassingRungIndex: null,
      reason: 'Carbon capture is not enabled for this scenario.',
    };
  }
  if (!ctx.input.ccs.pipelineExportConfirmed) {
    return indeterminate(
      'co2-export', label,
      'CO₂ export route or annual capacity is not confirmed; transport, injection, storage, and containment remain external studies.',
    );
  }
  const ceiling = linearCeiling({
    id: 'co2-export', label,
    limitValue: ctx.input.ccsExportCapacityTonnesPerYear ?? null,
    limitUnit: 'tonnes/year',
    baseConsumption: ctx.result.emissions.capturedCo2TonnesPerYear,
    baseSiteGrossMW: ctx.baseSiteGrossMW,
    undocumentedReason: 'Annual CO₂ export capacity is not documented.',
    notScalingReason: 'No CO₂ is captured, so export capacity cannot bound the fleet.',
  });
  return ceiling.status === 'bounded'
    ? {
      ...ceiling,
      reason: `Conditional on the scenario's ${ctx.input.annualOperatingHours} annual operating hours —`
        + ' higher dispatch breaches this ceiling at the same nameplate.',
    }
    : ceiling;
}

/**
 * Water CONSUMPTION. Unlike gas and CO2 this is COUPLED to the answer: the
 * data-center term scales with the served load, which is itself derived from the
 * capacity being tested. The coefficient therefore includes both legs:
 *
 *   consumption(G) = a·G + b·(served facility load)
 *                  = a·G + b·(firmNetPerGross·G · (1 − T&D))
 *
 * At a fixed fleet shape both legs are linear in G, so the ceiling is still
 * analytic — but it must be recomputed per rung on the block-count axis, where
 * `firmNetPerGrossMW` changes with the surviving fraction.
 *
 * FLAGGED GAP: `availableWaterConsumptionGalPerDay` bounds CONSUMPTION only.
 * There is no withdrawal limit anywhere in the scenario input, so a site can
 * clear this ceiling while being physically unable to WITHDRAW the water.
 */
function waterConsumptionCeiling(ctx: MaxPowerCeilingContext): GenerationMaxPowerConstraintCeiling {
  const label = 'Water consumption capacity';
  const total = ctx.result.water.totalConsumptionGalPerDay;
  if (ctx.input.availableWaterConsumptionGalPerDay == null || total == null) {
    return indeterminate(
      'water-consumption', label,
      'Water-source capacity or data-center consumption is missing; source adequacy is not assessed.',
    );
  }
  const ceiling = linearCeiling({
    id: 'water-consumption', label,
    limitValue: ctx.input.availableWaterConsumptionGalPerDay,
    limitUnit: 'gal/day',
    // The TOTAL already carries both the generation and the coupled DC leg,
    // because the sweep closes the load loop before reading it.
    baseConsumption: total,
    baseSiteGrossMW: ctx.baseSiteGrossMW,
    undocumentedReason: 'Water-source capacity is not documented.',
    notScalingReason: 'The scenario consumes no water, so source capacity cannot bound it.',
  });
  return ceiling.status === 'bounded'
    ? {
      ...ceiling,
      reason: 'Bounds CONSUMPTION only — no withdrawal limit exists in the scenario input,'
        + ' so a site can clear this ceiling and still be unable to withdraw the water.',
    }
    : ceiling;
}

/**
 * Receptor noise. The acoustic model derives level purely from documented source
 * data — NOTHING in it scales with MW rating. On the per-block-MW axis the noise
 * ceiling is therefore meaningless, and an `aggregate`-scope source is invariant
 * to block COUNT as well.
 *
 * This is the single largest honesty risk in the feature: without an explicit
 * `not-scaling` status a study would silently report "noise never binds" when
 * noise simply is not modelled against capacity at all.
 */
export function acousticAxisSensitivity(
  input: GenerationImpactScenarioInput,
  axis: 'equipment-block-count' | 'uniform-block-rating',
): 'scales-with-count' | 'invariant' {
  if (axis === 'uniform-block-rating') return 'invariant';
  const sources = input.noiseSources ?? [];
  if (sources.length === 0) return 'invariant';
  // An aggregate-scope datum describes the WHOLE plant, so adding blocks does
  // not raise it — the type's own doc says "Aggregate ignores equipment count".
  // `scope` exists only on the reference-spl variant.
  const anyAggregate = sources.some(
    s => s.noise?.kind === 'reference-spl' && s.noise.scope === 'aggregate',
  );
  return anyAggregate ? 'invariant' : 'scales-with-count';
}

function noiseCeiling(
  ctx: MaxPowerCeilingContext,
  axis: 'equipment-block-count' | 'uniform-block-rating',
): GenerationMaxPowerConstraintCeiling {
  const label = 'Receptor noise threshold';
  const receptorsWithThreshold = ctx.result.noise.filter(r => r.thresholdDbA != null);
  const base: GenerationMaxPowerConstraintCeiling = {
    id: 'receptor-noise', label,
    status: 'indeterminate',
    basis: 'enumerated',
    limitValue: null,
    limitUnit: 'dBA',
    intensityPerGrossMW: null,
    ceilingSiteGrossMW: null,
    conservativeCeilingSiteGrossMW: null,
    lastPassingRungIndex: null,
    reason: null,
  };

  if (receptorsWithThreshold.length === 0) {
    return { ...base, reason: 'No receptor carries a documented noise threshold.' };
  }

  if (acousticAxisSensitivity(ctx.input, axis) === 'invariant') {
    return {
      ...base,
      status: 'not-scaling',
      limitValue: Math.min(...receptorsWithThreshold.map(r => r.thresholdDbA as number)),
      reason: axis === 'uniform-block-rating'
        ? 'The acoustic model derives level from documented source data with NO dependence on MW rating,'
          + ' so no noise ceiling can be derived along this axis. This is NOT a finding that noise never binds.'
        : 'The scenario declares an aggregate-scope acoustic source, which is invariant to block count,'
          + ' so no noise ceiling can be derived. This is NOT a finding that noise never binds.',
    };
  }

  // Scales with count, but only via the enumerated sweep — the level law is
  // logarithmic and this module is restricted to exact arithmetic.
  return {
    ...base,
    limitValue: Math.min(...receptorsWithThreshold.map(r => r.thresholdDbA as number)),
    reason: 'Derived by enumeration; the acoustic level law is not evaluated analytically here.',
  };
}

/**
 * Every ceiling that can be derived analytically from one exact base solve.
 * Land is deliberately ABSENT — it is not linear in MW (the packer is a shelf
 * algorithm over a real polygon) and is resolved only by enumeration.
 */
export function deriveMaxPowerConstraintCeilings(
  ctx: MaxPowerCeilingContext,
  axis: 'equipment-block-count' | 'uniform-block-rating',
): readonly GenerationMaxPowerConstraintCeiling[] {
  return [
    gasFirmCapacityCeiling(ctx),
    gasDerateCeiling(ctx),
    waterConsumptionCeiling(ctx),
    co2ExportCeiling(ctx),
    noiseCeiling(ctx, axis),
  ];
}

/**
 * The smallest DOCUMENTED linear ceiling, used to bracket the sweep so the first
 * failing rung is always inside the ladder. Null when nothing is documented — in
 * which case the sweep runs to its search ceiling and the study reports
 * `search-ceiling-reached` rather than inventing a bound.
 */
export function smallestDocumentedCeilingSiteGrossMW(
  ceilings: readonly GenerationMaxPowerConstraintCeiling[],
): number | null {
  const bounded = ceilings
    .filter(c => c.status === 'bounded' && c.ceilingSiteGrossMW != null && Number.isFinite(c.ceilingSiteGrossMW))
    .map(c => c.ceilingSiteGrossMW as number);
  return bounded.length > 0 ? Math.min(...bounded) : null;
}
